import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export function SymposiumPage() {
  const programRef = useRef<HTMLDivElement>(null);

  const handleScrollToProgram = () => {
    programRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (window.location.hash === '#program' && programRef.current) {
      setTimeout(() => {
        programRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 200); // mali delay za sigurnost
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-4xl mx-auto px-4"
      >
        {/* Gumb za skrolanje na program */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleScrollToProgram}
            className="px-6 py-3 bg-blue-700 text-white rounded-full font-bold text-lg shadow hover:bg-blue-800 transition"
          >
            Finalni program
          </button>
        </div>
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-8 md:p-12">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center space-y-6"
            >
              <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                Simpozij Hrvatskog društva internističke onkologije i Hrvatskog društva onkoloških medicinskih sestara i tehničara
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-blue-100">
                "Multidisciplinarni pristup u liječenju tumora probavnih organa"
              </h2>
            </motion.div>
          </div>

          <div className="p-8 space-y-10">
            {/* Date and Location Card */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-xl text-center space-y-3 shadow-sm"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-2xl font-bold text-blue-900">22. svibnja 2025. godine</p>
              <p className="text-xl text-blue-800">od 13:00 do 19:30 sati</p>
              <div className="flex items-center justify-center space-x-2 text-blue-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-lg font-medium">Hotel International (dvorana Market)</p>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col md:flex-row justify-center gap-4"
            >
              <Link
                to="/simpozij-prijava"
                className="group relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-blue-600 rounded-full overflow-hidden transition-all duration-300 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <span className="relative z-10">Prijava sažetka</span>
                <div className="absolute inset-0 bg-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
              </Link>
              
              <Link
                to="/simpozij-registracija"
                className="group relative inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-white bg-green-600 rounded-full overflow-hidden shadow-xl transition-all duration-300 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 animate-pulse hover:animate-none transform hover:scale-105"
              >
                <span className="relative z-10 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Prijavi se na simpozij
                </span>
                <div className="absolute inset-0 bg-green-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
              </Link>
              
              <Link
                to="/simpozij-upload-prezentacije"
                className="group relative inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-purple-600 rounded-full overflow-hidden transition-all duration-300 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
              >
                <span className="relative z-10 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12" />
                  </svg>
                  Upload gotove prezentacije
                </span>
                <div className="absolute inset-0 bg-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
              </Link>
            </motion.div>

            {/* Description */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="prose max-w-none space-y-4"
            >
              <p className="text-gray-700 text-lg leading-relaxed">
                Čast nam je pozvati vas na Simpozij "Multidisciplinarni pristup u liječenju
                tumora probavnih organa" u organizaciji Hrvatskog društva internističke onkologije i Hrvatskog društva
                onkoloških medicinskih sestara i tehničara. Simpozij je namijenjen medicinskim sestrama i liječnicima.
              </p>
              <p className="text-blue-800 font-medium text-lg bg-blue-50 p-4 rounded-lg inline-block">
                Simpozij će zainteresirani moći pratiti i hibridno.
              </p>
            </motion.div>

            {/* Topics */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl border border-blue-100 p-6 shadow-sm"
            >
              <h3 className="text-2xl font-bold text-blue-900 mb-6">Teme simpozija:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Gensko profiliranje tumora probavnih organa",
                  "Kirurško liječenje tumora probavnih organa",
                  "Uloga radiologa u liječenju tumora probavnih organa",
                  "Radioterapija tumora probavnih organa",
                  "Sustavno liječenje tumora probavnih organa",
                  "Prehrana bolesnika oboljelih od tumora probavnih organa"
                ].map((topic, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-700">{topic}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Committee */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6"
            >
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Organizacijski i stručni odbor:</h3>
              <div className="space-y-3">
                {[
                  "doc.dr.sc. Juraj Prejac, dr.med.",
                  "Jasminka Miličević, mag.med. techn."
                ].map((member, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-gray-700 font-medium">{member}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Points */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-white rounded-xl border border-blue-100 p-6 shadow-sm"
            >
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Bodovanje:</h3>
              <p className="text-gray-700 mb-4">
                Simpozij će biti bodovan od strane HKMS i HLK sukladno pravilniku o bodovanju.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="font-semibold text-blue-800 mb-2">HKMS</div>
                  <div className="space-y-1 text-gray-700">
                    <div>Predavač: 8 bodova</div>
                    <div>Polaznik: 5 bodova</div>
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="font-semibold text-blue-800 mb-2">HLK</div>
                  <div className="space-y-1 text-gray-700">
                    <div>Predavač: 9 bodova</div>
                    <div>Polaznik: 7 bodova</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Registration */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-8"
            >
              <h3 className="text-2xl font-bold mb-6">Kotizacija i prijava:</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-lg">Kotizacija za sudjelovanje: <span className="font-bold">90 EUR</span></p>
                </div>
                <div className="bg-blue-500 bg-opacity-30 rounded-lg p-4">
                  <p className="font-mono text-lg">IBAN: HR9623600001102178796</p>
                  <p className="text-blue-100 mt-1">S naznakom "Kotizacija za simpozij"</p>
                </div>
              </div>
            </motion.div>

            {/* Technical Organizer */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="border-t border-blue-100 pt-6"
            >
              <h3 className="text-2xl font-bold text-blue-900 mb-4">Tehnički organizator:</h3>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="space-y-2 text-gray-700">
                  <p className="font-semibold">Studio Arena d.o.o.</p>
                  <p>Ulica dr. Luje Naletilića 94b, 10 000 Zagreb</p>
                  <p>OIB: 28869851978</p>
                  <div className="flex items-center space-x-2 text-blue-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <a href="mailto:info@studio-arena.hr" className="hover:underline">info@studio-arena.hr</a>
                  </div>
                  <div className="flex items-center space-x-2 text-blue-600">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <a href="http://www.studio-arena.hr" target="_blank" rel="noopener noreferrer" className="hover:underline">www.studio-arena.hr</a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        {/* Finalni program sekcija */}
        <div ref={programRef} id="finalni-program" className="mt-16 bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Finalni program</h2>
          <div className="flex flex-col gap-4">
            {/* 13:00-13:30 */}
            <div className="bg-blue-50 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">13:00-13:30</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Registracija sudionika</div>
              </div>
            </div>
            {/* 13:30-13:45 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">13:30-13:45</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Liječenje lokalno uznapredovalog raka jednjaka</div>
                <div className="text-gray-600 text-sm mt-1">Soče, M.</div>
              </div>
            </div>
            {/* 13:45-14:00 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">13:45-14:00</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Prikaz slučaja metastatskog adenokarcinoma jednjaka</div>
                <div className="text-gray-600 text-sm mt-1">Soldo, A., Novak, M.</div>
              </div>
            </div>
            {/* Roche simpozij samo 14:00-14:45 u plavom boxu */}
            <div className="bg-blue-100 rounded-xl p-4 shadow-sm border-l-4 border-blue-400">
              <div className="font-bold text-blue-900 mb-2">Satelitski simpozij Roche - 14:00-14:45</div>
              <div className="font-semibold text-gray-800">Uloga sveobuhvatnog genomskog profiliranja u tumorima probavnih organa</div>
              <div className="text-gray-600 text-sm mt-1">Gotovac Jerčić, K.</div>
              <div className="font-semibold text-gray-800 mt-3">Izazovi u liječenju bolesnika s hepatocelularnim karcinomom</div>
              <div className="text-gray-600 text-sm mt-1">Prejac, J.</div>
            </div>
            {/* 14:45-15:00 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">14:45-15:00</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Važnost enteralne prehrane i suportivnih dodataka u procesu liječenja maligne bolesti</div>
                <div className="text-gray-600 text-sm mt-1">Čop, I., Bešlić, D., Horvatić, J.</div>
              </div>
            </div>
            {/* Swixx Biopharma simpozij samo 15:00-15:45 u plavom boxu */}
            <div className="bg-blue-100 rounded-xl p-4 shadow-sm border-l-4 border-blue-400">
              <div className="font-bold text-blue-900 mb-2">Satelitski simpozij Swixx Biopharma - 15:00-15:45</div>
              <div className="font-semibold text-gray-800">Nivolumab: New Opportunity in Gastrointestinal Cancer</div>
              <div className="text-gray-600 text-sm mt-1">Prejac, J., Šabarić, V., Soče, M.</div>
            </div>
            {/* 15:45-16:00 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">15:45-16:00</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Sestrinske intervencije kod liječenja tumora probavnog sustava pametnim lijekovima</div>
                <div className="text-gray-600 text-sm mt-1">Ormanec, T., Žderić, I., Puhelek, A., Galo, S., Bižić, R.</div>
              </div>
            </div>
            {/* 16:00-16:15 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">16:00-16:15</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Sestrinska skrb i gensko profiliranje tumora probavnih organa - izazovi i odgovornosti</div>
                <div className="text-gray-600 text-sm mt-1">Vlahek, Lj., Kosier, M., Janeš, S., Užarević, S., Grubišić, J.</div>
              </div>
            </div>
            {/* Amgen simpozij 16:15-16:45 u plavom boxu */}
            <div className="bg-blue-100 rounded-xl p-4 shadow-sm border-l-4 border-blue-400">
              <div className="font-bold text-blue-900 mb-2">Satelitski simpozij Amgen - 16:15-16:45</div>
              <div className="font-semibold text-gray-800">Multidisciplinarni pristup u liječenju tumora probavnih organa</div>
              <div className="text-gray-600 text-sm mt-1">Prejac, J., Librenjak, N., Kekez, D.</div>
            </div>
            {/* Pauza */}
            <div className="bg-blue-50 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">16:45-17:00</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Pauza za kavu</div>
              </div>
            </div>
            {/* 17:00-17:15 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">17:00-17:15</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Sustavno liječenje ranog HCC-a</div>
                <div className="text-gray-600 text-sm mt-1">Kekez, D.</div>
              </div>
            </div>
            {/* Astra Zeneca simpozij 17:15-18:00 u plavom boxu */}
            <div className="bg-blue-100 rounded-xl p-4 shadow-sm border-l-4 border-blue-400">
              <div className="font-bold text-blue-900 mb-2">Satelitski simpozij Astra Zeneca - 17:15-18:00</div>
              <div className="font-semibold text-gray-800">Sekvencijsko liječenje hepatocelularnog karcinoma</div>
              <div className="text-gray-600 text-sm mt-1">Bilanović, Ž., Adžić, G., Lučev, H.</div>
            </div>
            {/* 18:00-18:15 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">18:00-18:15</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Neoadjuvantno liječenje raka debelog crijeva</div>
                <div className="text-gray-600 text-sm mt-1">Librenjak, N.</div>
              </div>
            </div>
            {/* 18:15-18:30 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">18:15-18:30</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Nuspojave imunoterapije</div>
                <div className="text-gray-600 text-sm mt-1">Šabarić, V.</div>
              </div>
            </div>
            {/* Genesis Pharma simpozij 18:30-19:00 u plavom boxu */}
            <div className="bg-blue-100 rounded-xl p-4 shadow-sm border-l-4 border-blue-400">
              <div className="font-bold text-blue-900 mb-2">Satelitski simpozij Genesis Pharma - 18:30-19:00</div>
              <div className="font-semibold text-gray-800">Pemazyre (pemigatinib) u liječenju lokalno uznapredovalog ili metastatskog kolangiokarcinoma s fuzijom ili preraspoređivanjem FGFR2</div>
              <div className="font-semibold text-gray-800 mt-3">Qinlock (ripretinib) u liječenju uznapredovalog gastrointestinalnog stromalnog tumora</div>
              <div className="text-gray-600 text-sm mt-1">Goršić, I., Librenjak, N.</div>
            </div>
            {/* 19:00-19:15 */}
            <div className="bg-white rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm border border-blue-100">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">19:00-19:15</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Perioperativno liječenje raka želuca</div>
                <div className="text-gray-600 text-sm mt-1">Lučev, H.</div>
              </div>
            </div>
            {/* 19:15-19:30 */}
            <div className="bg-blue-50 rounded-xl p-4 flex flex-col md:flex-row md:items-center md:gap-6 shadow-sm">
              <div className="text-blue-800 font-bold text-lg md:w-32 mb-2 md:mb-0">19:15-19:30</div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">Evaluacija i zaključci</div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <img src="/merck.jpg" alt="Merck" className="w-full max-w-md h-auto" />
        </div>
      </motion.div>
    </div>
  );
} 